<template>
  <header class="nav ws" :class="{ 'is-stuck': stuck, 'is-open': drawer, 'nav--tight': tight }">
    <div class="nav__inner">
      <router-link class="nav__brand" to="/" aria-label="Taoify">
        <img class="nav__logo nav__logo--w" src="/website/img/logo-white.png" alt="Taoify">
        <img class="nav__logo nav__logo--d" src="/website/img/logo.png" alt="Taoify">
      </router-link>

      <nav class="nav__menu" :aria-label="$t('主导航')">
        <router-link
          v-for="m in menus"
          :key="m.to"
          :to="m.to"
          :class="{ on: isActive(m), nav__slim: m.slim }"
        >{{ $t(m.label) }}</router-link>
      </nav>

      <div class="nav__right">
        <!-- 20 语种下拉（与店主后台同一套） -->
        <div class="lang" :class="{ 'is-open': langOpen }">
          <button class="lang__btn" type="button" aria-haspopup="true" :aria-expanded="langOpen ? 'true' : 'false'" @click.stop="langOpen = !langOpen">
            <svg class="ico ico--xs"><use href="#i-globe"/></svg>
            <span>{{ currentLang.label }}</span>
            <svg class="lang__caret" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>

          <div class="lang__menu" role="menu">
            <div class="lang__head">
              <span>{{ $t('语言') }}</span>
              <em>{{ $t('20 种语言 · 买家看自己的母语') }}</em>
            </div>
            <div class="lang__grid">
              <button
                v-for="l in langs"
                :key="l.sign"
                type="button"
                role="menuitem"
                :class="{ on: l.sign === current }"
                @click="chooseLang(l)"
              ><span>{{ l.label }}</span><em>{{ l.short }}</em></button>
            </div>
          </div>
        </div>

        <!-- 未登录：登录 + 免费开店 -->
        <template v-if="!userinfo">
          <a class="nav__login" :href="adminUrl" target="_blank">{{ $t('登录') }}</a>
          <router-link class="nav__cta" to="/sign-up">{{ $t('免费开店') }}</router-link>
        </template>

        <!-- 已登录：模板中心 + 头像菜单（沿用原用户菜单逻辑） -->
        <template v-else>
          <router-link to="/user-index" class="nav__studio">
            <svg class="ico ico--xs"><use href="#i-sparkle"/></svg>
            {{ $t('模板中心') }}
          </router-link>
          <v-menu open-on-hover bottom offset-y close-on-content-click open-delay="120" close-delay="80" nudge-bottom="6">
            <template v-slot:activator="{ on, attrs }">
              <button class="nav__avatar-btn" type="button" v-bind="attrs" v-on="on">
                <span class="nav__avatar">
                  <img v-if="userinfo.avatar" :src="userinfo.avatar" alt="">
                  <svg class="ico" v-else><use href="#i-user"/></svg>
                </span>
              </button>
            </template>
            <v-card width="230" class="appbar-user-menu">
              <div class="user-head">
                <v-avatar size="44" class="user-head-avatar">
                  <img v-if="userinfo.avatar" :src="userinfo.avatar" alt="">
                  <v-icon v-else size="24" color="#fff">mdi-account</v-icon>
                </v-avatar>
                <div class="user-head-meta">
                  <p class="user-head-name">{{ userinfo.nickname || $t('用户') }}</p>
                  <p class="user-head-mail">{{ userinfo.mobile || userinfo.email }}</p>
                </div>
              </div>
              <v-divider class="my-0"></v-divider>
              <div class="user-menu-list">
                <router-link to="/user-index" class="text-decoration-none">
                  <div class="user-menu-item">
                    <span class="menu-icon mi-purple"><v-icon size="18" color="#7331FF">mdi-creation</v-icon></span>
                    <span class="menu-text">{{ $t('模板中心') }}</span>
                    <v-icon size="16" color="#C3CAD6" class="ml-auto">mdi-chevron-right</v-icon>
                  </div>
                </router-link>
                <a :href="adminUrl" target="_blank" class="text-decoration-none">
                  <div class="user-menu-item">
                    <span class="menu-icon mi-blue"><v-icon size="18" color="#5268F2">mdi-store-outline</v-icon></span>
                    <span class="menu-text">{{ $t('小店管理') }}</span>
                    <v-icon size="16" color="#C3CAD6" class="ml-auto">mdi-chevron-right</v-icon>
                  </div>
                </a>
              </div>
              <v-divider class="my-0"></v-divider>
              <div class="user-menu-item logout-item" @click="logout">
                <span class="menu-icon mi-red"><v-icon size="18" color="#FF5252">mdi-logout</v-icon></span>
                <span class="menu-text">{{ $t('退出登录') }}</span>
              </div>
            </v-card>
          </v-menu>
        </template>

        <button class="nav__burger" type="button" :aria-label="$t('菜单')" :aria-expanded="drawer ? 'true' : 'false'" @click="drawer = !drawer">
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

    <!-- 移动端抽屉 -->
    <div class="nav__scrim" v-if="drawer" @click="drawer = false"></div>
    <div class="nav__drawer">
      <router-link
        v-for="m in menus"
        :key="'d-' + m.to"
        :to="m.to"
        @click.native="drawer = false"
      >{{ $t(m.label) }}</router-link>
      <div class="nav__drawer-foot">
        <a class="btn btn--ghost btn--sm" v-if="!userinfo" :href="adminUrl" target="_blank">{{ $t('登录') }}</a>
        <button type="button" class="btn btn--primary btn--sm" v-if="!userinfo" @click="goSignUp">{{ $t('免费开店') }}</button>
      </div>
    </div>
  </header>
</template>

<script>
import { SITE_LANGS, findLang, isRtlLocale } from '@/lang/langs'
import { updatePageSEO } from '@/utils/seo'
import { loadLocale } from '@/lang'

export default {
  name: 'WebsiteHeader',
  data () {
    return {
      stuck: false,
      drawer: false,
      langOpen: false,
      // 横排菜单放不下时置真：收起次要项（模板市场 / 模板中心），
      // 否则菜单会溢出容器、盖到右侧语言选择器和按钮上（葡语、老挝语这类长文案必现）
      tight: false,
      langs: SITE_LANGS,
      menus: [
        { to: '/', label: '首页' },
        { to: '/feature', label: '功能' },
        { to: '/ai-staff', label: 'AI 员工' },
        { to: '/logistics', label: '物流' },
        { to: '/payment', label: '收款' },
        { to: '/pricing', label: '价格' },
        { to: '/cases', label: '案例' },
        // slim：横排空间不够时先隐藏（页脚「产品」列有同样入口），窄屏统一进抽屉
        { to: '/template-index', label: '模板市场', slim: true },
        { to: '/question', label: '帮助中心' },
      ],
    }
  },
  computed: {
    current () {
      return this.$store.state.langdef || 'zh-CN'
    },
    currentLang () {
      return findLang(this.current)
    },
    userinfo () {
      return this.$store.state.USERINFO
    },
    adminUrl () {
      return `${this.$store.state.locationOrigin || window.location.origin}/manger_admin`
    },
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onResize)
    document.addEventListener('click', this.onDocClick)
    document.addEventListener('keydown', this.onKeydown)
    this.onScroll()
    this.applyHtmlAttrs(this.current)
    /* 量「菜单放不放得下」要等三件事落定：字体、语言包（异步 import）、
       首帧布局。只测一次会测在中文短文案上，切到葡语长文案后就不管了，
       所以分散补测几次；每次都是幂等的（只判断当前是否溢出）。 */
    ;[0, 150, 600, 1500].forEach(t => setTimeout(this.checkNavFit, t))
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.checkNavFit).catch(() => {})
    }
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('click', this.onDocClick)
    document.removeEventListener('keydown', this.onKeydown)
  },
  watch: {
    $route () {
      // 切页面收起抽屉与语言菜单
      this.drawer = false
      this.langOpen = false
    },
    current () {
      // 换语言 = 换文案长度，菜单宽度要重新量
      this.$nextTick(this.checkNavFit)
    },
    /* 语言包是按需下载的：首屏先按中文（key 原文）渲染，pt/lo 这些长文案
       是异步到位后才替换的 —— 只监听 store 里的 langdef 不够，必须监听
       i18n.locale 本身，否则「首屏量过一遍就不再看」会漏掉后面变长的情况。 */
    '$i18n.locale' () {
      this.$nextTick(this.checkNavFit)
    },
    userinfo () {
      this.$nextTick(this.checkNavFit)
    },
  },
  methods: {
    onResize () {
      this.checkNavFit()
    },
    /**
     * 横排菜单是否放得下。
     * .nav__menu 是 flex:1 + overflow:hidden，clientWidth 是它拿到的宽度，
     * scrollWidth 是文案实际需要的宽度，前者小就说明挤了 —— 此时收起次要项。
     * 逐语种写死断点不现实（20 种语言长度差一倍），所以用实测。
     */
    checkNavFit () {
      const el = this.$el && this.$el.querySelector('.nav__menu')
      if (!el) return
      this.tight = false
      this.$nextTick(() => {
        const menu = this.$el && this.$el.querySelector('.nav__menu')
        if (!menu) return
        // 先按「不收起」量一次；放不下才收起，避免窄屏上永远收起
        this.tight = menu.scrollWidth > menu.clientWidth + 1
      })
    },
    onScroll () {
      this.stuck = window.scrollY > 24
    },
    onDocClick (e) {
      if (!this.$el.contains(e.target)) {
        this.langOpen = false
        this.drawer = false
      }
    },
    onKeydown (e) {
      if (e.key === 'Escape') {
        this.langOpen = false
        this.drawer = false
      }
    },
    isActive (m) {
      const p = this.$route.path
      if (m.to === '/') return p === '/'
      if (m.to === '/template-index') return p.indexOf('/template') === 0 || p === '/user-index' || p === '/balance' || p === '/buyed' || p === '/income' || p === '/withdrawal' || p === '/consume'
      if (m.to === '/question') return p === '/question' || p === '/help'
      return p === m.to
    },
    chooseLang (l) {
      this.langOpen = false
      if (l.sign === this.current) return
      // 词表是按需下载的，先等这份到位再切 locale，避免闪一下中文
      loadLocale(l.sign).then(() => {
        this.$i18n.locale = l.sign
        this.$store.commit('setLangDef', l.sign)
        this.applyHtmlAttrs(l.sign)
        // 标题/描述跟着换语言，否则停在上一门语言直到下次跳页
        updatePageSEO(this.$route.path, l.sign)
        this.$nextTick(this.checkNavFit)
      })
    },
    applyHtmlAttrs (sign) {
      const html = document.documentElement
      html.setAttribute('lang', sign)
      // 设计稿只为英文排了专用行高/字距，其余语种用默认（中文）版式
      html.setAttribute('data-lang', sign === 'en' ? 'en' : 'zh')
      html.setAttribute('dir', isRtlLocale(sign) ? 'rtl' : 'ltr')
      if (this.$vuetify) this.$vuetify.rtl = isRtlLocale(sign)
    },
    goSignUp () {
      this.drawer = false
      this.$router.push('/sign-up')
    },
    logout () {
      this.$store.commit('setUSERINFO', null)
      localStorage.removeItem('DROPSHOPUSERINFO')
      localStorage.removeItem('dropshoplocalconfig')
      localStorage.removeItem('SHOPLIST')
      localStorage.removeItem('defaultShopid')
      localStorage.removeItem('DROPSHOPINFO')
      localStorage.removeItem('shop_web_domain')
      this.$router.push('/sign-up')
    },
  },
}
</script>

<style lang="scss" scoped>
/* 登录态新增的两个入口（沿用设计稿视觉语言） */
.nav__studio{
  display:flex;align-items:center;gap:6px;
  font-size:14px;font-weight:600;color:rgba(255,255,255,.9);
  transition:color .18s;
}
.nav.is-stuck .nav__studio, body.nav-solid .nav__studio{color:#4B5761}
.nav__studio:hover{color:#fff}
.nav.is-stuck .nav__studio:hover, body.nav-solid .nav__studio:hover{color:var(--p)}
.nav__avatar-btn{background:none;border:none;padding:0;cursor:pointer;display:flex}
.nav__avatar{
  width:34px;height:34px;border-radius:50%;overflow:hidden;
  display:flex;align-items:center;justify-content:center;
  background:rgba(115,49,255,.25);border:2px solid rgba(255,255,255,.5);
}
.nav__avatar .ico{width:18px;height:18px;color:#fff}
.nav.is-stuck .nav__avatar, body.nav-solid .nav__avatar{border-color:rgba(15,33,40,.12);background:var(--p-light)}
.nav.is-stuck .nav__avatar .ico, body.nav-solid .nav__avatar .ico{color:var(--p)}
.nav__avatar img{width:100%;height:100%;object-fit:cover}
</style>

<style lang="scss">
/* 头像弹出菜单（v-menu 内容 teleport 到 body，scoped 不生效，必须全局；沿用原后台样式） */
.appbar-user-menu {
  border-radius: 14px !important;
  overflow: hidden;
  border: 1px solid #EEF1F5;
  box-shadow: 0 12px 36px rgba(15, 52, 96, 0.14) !important;

  .user-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, #7331FF 0%, #5B21D8 100%);

    .user-head-avatar {
      border: 2px solid #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
      flex-shrink: 0;
    }

    .user-head-meta {
      min-width: 0;

      .user-head-name {
        font-size: 15px;
        font-weight: 700;
        color: #fff;
        margin-bottom: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-head-mail {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.75);
        margin-bottom: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .user-menu-list {
    padding: 6px;
  }

  .user-menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;

    .menu-icon {
      width: 30px;
      height: 30px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &.mi-purple { background: #7331FF; }
      &.mi-blue { background: #5268F2; }
      &.mi-red { background: #FF5252; }

      .v-icon { color: #fff !important; }
    }

    .menu-text {
      font-size: 13px;
      font-weight: 600;
      color: #3A4657;
    }

    &:hover {
      background: rgba(115, 49, 255, 0.06);

      .menu-text { color: #7331FF; }
    }

    &.logout-item {
      padding: 10px 12px;
      margin: 0 6px 6px;
      width: calc(100% - 12px);

      .menu-text { color: #FF5252; }

      &:hover { background: rgba(255, 82, 82, 0.08); }
    }
  }
}
</style>
